import React, { useState, useEffect } from 'react';
import { X, Loader2, Package, Search, ChevronDown, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import menuitemService from '../../../../services/menuitemService';
import inventoryService from '../../../../services/inventoryService';

// ── Spelling-match helpers (used only when there is no exact category match) ─────

// Levenshtein edit distance between two strings.
const levenshtein = (a = '', b = '') => {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
};

// Normalise a name for fuzzy comparison: lowercase, collapse punctuation/whitespace.
const normaliseName = (s = '') =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();

// Similarity ratio in [0, 1] derived from edit distance.
const similarity = (a, b) => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
};

// Only auto-select a spelling match when it's genuinely close, so unrelated
// categories are never picked. Tune this if you want it stricter/looser.
const CATEGORY_MATCH_THRESHOLD = 0.8;

export default function ImportFromMenuModal({ onClose, onSuccess, uomList = [] }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);   // ← fetched internally
  const [selectedItems, setSelectedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch menu items and inventory categories in parallel
      const [allItems, cats] = await Promise.all([
        menuitemService.getAll({ isFinishedItem: true }),
        inventoryService.getCategories(),
      ]);
      const finishedItems = (allItems || []).filter(item => item.isFinishedItem === true);
      setMenuItems(finishedItems);
      setCategories(cats || []);
    } catch (e) {
      console.error('Failed to load data:', e);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ... rest of the component is IDENTICAL to the previous version

  const filteredItems = menuItems.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.categoryName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Match the menu item's category to an inventory category:
  //   1) EXACT name match (same casing, trimmed) — original behaviour, wins first.
  //   2) If there is NO exact match, fall back to the closest SPELLING match
  //      (case/space/punctuation-insensitive + edit-distance). This only selects
  //      when the names are genuinely close (>= CATEGORY_MATCH_THRESHOLD), so an
  //      unrelated category is never auto-selected when nothing really matches.
  const resolveDefaultCategoryId = (item) => {
    const menuCategoryName = (item?.categoryName || '').trim();
    if (!menuCategoryName || categories.length === 0) return '';

    // 1) Exact match (unchanged)
    const exact = categories.find(
      (cat) => (cat.name || '').trim() === menuCategoryName
    );
    if (exact) return String(exact.id);

    // 2) Spelling-based match (only when no exact match exists)
    const target = normaliseName(menuCategoryName);
    if (!target) return '';

    let best = null;
    let bestScore = 0;
    for (const cat of categories) {
      const candidate = normaliseName(cat.name);
      if (!candidate) continue;
      // A normalised equality (e.g. only casing/spacing differs) scores a full 1.
      const score = candidate === target ? 1 : similarity(target, candidate);
      if (score > bestScore) {
        bestScore = score;
        best = cat;
      }
    }

    return best && bestScore >= CATEGORY_MATCH_THRESHOLD ? String(best.id) : '';
  };

  // Default UoM: Pieces, falling back to Unit if Pieces isn't available.
  const resolveDefaultUomId = () => {
    const byName = (name) =>
      uomList.find((uom) => (uom.name || '').trim().toLowerCase() === name);
    const pieces = byName('pieces') || byName('piece');
    if (pieces) return String(pieces.id);
    const unit = byName('unit') || byName('units');
    if (unit) return String(unit.id);
    return '';
  };

  const toggleItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev[itemId]) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      const item = menuItems.find((m) => m.id === itemId);
      return {
        ...prev,
        [itemId]: {
          currentStock: 0,
          minimumStock: 5,
          unitCost: item?.basePrice || 0,
          uomId: resolveDefaultUomId(),
          categoryId: resolveDefaultCategoryId(item),
        },
      };
    });
  };

  const toggleAll = () => {
    if (Object.keys(selectedItems).length === filteredItems.length) {
      setSelectedItems({});
    } else {
      const all = {};
      filteredItems.forEach((item) => {
        all[item.id] = selectedItems[item.id] || {
          currentStock: 0,
          minimumStock: 5,
          unitCost: item.basePrice || 0,
          uomId: resolveDefaultUomId(),
          categoryId: resolveDefaultCategoryId(item),
        };
      });
      setSelectedItems(all);
    }
  };

  const updateItemField = (itemId, field, value) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const selectedCount = Object.keys(selectedItems).length;

  const handleImport = async () => {
    if (selectedCount === 0) {
      toast.error('Select at least one item to import');
      return;
    }

    for (const [itemId, data] of Object.entries(selectedItems)) {
      if (!data.uomId) {
        const item = menuItems.find((m) => m.id === Number(itemId));
        toast.error(`Please select a UoM for "${item?.name || itemId}"`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        items: Object.entries(selectedItems).map(([menuItemId, data]) => {
          const item = menuItems.find((m) => m.id === Number(menuItemId));
          return {
            menuItemId: Number(menuItemId),
            name: item?.name || '',
            currentStock: parseFloat(data.currentStock) || 0,
            minimumStock: parseFloat(data.minimumStock) || 0,
            unitCost: parseFloat(data.unitCost) || 0,
            uomId: Number(data.uomId),
            categoryId: data.categoryId ? Number(data.categoryId) : null,
          };
        }),
      };

      const response = await inventoryService.importFromMenu(payload);
      toast.success(`${response?.created || selectedCount} item(s) imported successfully!`);
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error('Import failed:', e);
      toast.error('Import failed: ' + (e.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Shared column layout: checkbox=1, name=3, stock=2, min=2, cost=2, uom=2, category=2 → 14 cols
  const gridCols = 'grid grid-cols-[1.5rem_1fr_repeat(4,7rem)_7rem]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-white">Import Finished Items from Menu</h3>
            <p className="text-teal-100 text-sm mt-0.5">
              Create inventory items from menu items marked as "Finished Item"
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-teal-600" />
              <span className="ml-3 text-slate-500">Loading menu items...</span>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <Package size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">No finished menu items found</p>
              <p className="text-slate-400 text-sm mt-1">
                Mark menu items as "Finished Item" in Menu Management first, or all finished items are already imported.
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCount === filteredItems.length && filteredItems.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-200"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Select All ({filteredItems.length} items)
                  </span>
                </label>
                {selectedCount > 0 && (
                  <span className="text-sm text-teal-600 font-semibold">{selectedCount} selected</span>
                )}
              </div>

              {/* Table Header */}
              <div className={`${gridCols} gap-2 px-3 py-2 bg-slate-100 rounded-t-lg text-xs font-semibold text-slate-600 uppercase tracking-wide items-center`}>
                <div></div>
                <div>Menu Item</div>
                <div>Current Stock</div>
                <div>Min Stock</div>
                <div>Unit Cost (₹)</div>
                <div>UoM</div>
                <div>Category</div>
              </div>

              {/* Item Rows */}
              <div className="border border-slate-200 rounded-b-lg divide-y divide-slate-100">
                {filteredItems.map((item) => {
                  const isSelected = !!selectedItems[item.id];
                  const data = selectedItems[item.id] || {};

                  return (
                    <div
                      key={item.id}
                      className={`${gridCols} gap-2 px-3 py-3 items-center transition-colors ${
                        isSelected ? 'bg-teal-50/50' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Checkbox */}
                      <div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItem(item.id)}
                          className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-200"
                        />
                      </div>

                      {/* Item Name */}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">{item.categoryName || 'Uncategorized'}</p>
                      </div>

                      {/* Current Stock */}
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={data.currentStock ?? ''}
                          onChange={(e) => updateItemField(item.id, 'currentStock', e.target.value)}
                          disabled={!isSelected}
                          placeholder="0"
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>

                      {/* Min Stock */}
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={data.minimumStock ?? ''}
                          onChange={(e) => updateItemField(item.id, 'minimumStock', e.target.value)}
                          disabled={!isSelected}
                          placeholder="5"
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>

                      {/* Unit Cost */}
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={isSelected ? (data.unitCost ?? '') : (item.basePrice || '')}
                          onChange={(e) => updateItemField(item.id, 'unitCost', e.target.value)}
                          disabled={!isSelected}
                          placeholder="0.00"
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>

                      {/* UoM */}
                      <div>
                        <select
                          value={data.uomId ?? ''}
                          onChange={(e) => updateItemField(item.id, 'uomId', e.target.value)}
                          disabled={!isSelected}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-100 disabled:text-slate-400 appearance-none"
                        >
                          <option value="">Select</option>
                          {uomList.map((uom) => (
                            <option key={uom.id} value={uom.id}>
                              {uom.name} ({uom.symbol})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Category */}
                      <div>
                        <select
                          value={data.categoryId ?? ''}
                          onChange={(e) => updateItemField(item.id, 'categoryId', e.target.value)}
                          disabled={!isSelected}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-100 disabled:text-slate-400 appearance-none"
                        >
                          <option value="">Select</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={String(cat.id)}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-white flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <AlertCircle size={14} />
            <span>Inventory items will be created with the same name as the menu item</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={submitting || selectedCount === 0}
              className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Package size={16} />
                  Import {selectedCount > 0 ? `${selectedCount} Item${selectedCount > 1 ? 's' : ''}` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}